import { Directionality } from '@angular/cdk/bidi';
import { NgClass } from '@angular/common';
import { CdkStepper } from '@angular/cdk/stepper';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    ViewEncapsulation,
    inject,
    input
} from '@angular/core';

import { ApContentService } from '@ap/content';

import content from './stepper-header.content.json';

/** The header which shows on which step the user is out of all the steps. It registers keyboard and click prompts on the steps which are selected. The component allows for dynamic updating of amount of steps and current step. */
@Component({
    selector: 'ap-stepper-header',
    templateUrl: './stepper-header.component.html',
    styleUrls: ['./stepper-header.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    preserveWhitespaces: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgClass]
})
export class ApStepperHeaderComponent extends CdkStepper {
    private readonly contentService = inject(ApContentService);

    /**
     * Progress of stepper.
     * Defaults to 0.
     */
    public readonly stepProgress = input(0);

    protected activatedIndex: number;
    protected navigationEmitter: EventEmitter<number>;
    protected visitStates: boolean[] = [];

    // This should be an ApStepComponent, but importing this in this file creates a cyclical import.
    // File structure should be changed to fix this
    private stepComponents: any[];
    private trackVisitedStateInternal: boolean;

    private readonly forwardKeys: string[] = ['ArrowDown', 'ArrowRight'];
    private readonly backwardKeys: string[] = ['ArrowUp', 'ArrowLeft'];

    readonly uniqueId = input<string>();

    public constructor() {
        const changeDetectorRef = inject(ChangeDetectorRef);
        const dir = inject(Directionality, { optional: true });
        const elementRef = inject(ElementRef<HTMLElement>);
        super(dir, changeDetectorRef, elementRef);
        this.contentService.addContentItems(content);
    }

    /**
     * Singleton set up for the navigation emitter. Which will transfer the clicks and keyboard prompts up to the parent component
     */
    @Output() public get navigation(): EventEmitter<number> {
        if (!this.navigationEmitter) {
            this.navigationEmitter = new EventEmitter<number>();
        }
        return this.navigationEmitter;
    }

    /**
     * The amount of steps, which can be set up with a number
     */
    @Input() public set numberOfSteps(maxAmountOfSteps: number) {
        this.trackVisitedStateInternal = true;
        this.visitStates = [];
        for (let stepNumber = 0; stepNumber < maxAmountOfSteps; stepNumber++) {
            this.visitStates.push(stepNumber <= this.activatedIndex);
        }
        this.activeIndex = this.activatedIndex;
    }

    /**
     * Set the visited states with an array of booleans where the steps which are visited are set to true or a QueryList of ApStepComponent
     */
    @Input() public set visitedState(steps: boolean[] | any[]) {
        if (steps.length === 0) {
            return;
        }
        if (typeof steps[0] === 'boolean') {
            this.setVisitedState(steps as boolean[]);
        } else {
            this.setStepComponents(steps);
        }
        this.trackVisitedStateInternal = false;
    }

    /**
     * The current step/index in the list of steps
     */
    @Input() public set activeIndex(activeIndex: number) {
        if (activeIndex < 0) {
            this.activatedIndex = 0;
            return;
        }
        if (this.trackVisitedStateInternal) {
            this.updateInternalVisitedState(activeIndex);
        }
        if (activeIndex > this.visitStates.length) {
            this.activatedIndex = this.visitStates.length - 1;
            return;
        }
        this.activatedIndex = activeIndex;
    }

    protected hasVisited(step: number): boolean {
        if (step >= this.visitStates.length || step < 0) {
            return false;
        }
        if (this.stepComponents) {
            return this.stepComponents[step].completed || this.activatedIndex === step;
        }
        return this.visitStates[step];
    }

    protected click(step: number): void {
        if (!this.navigationEmitter || !this.hasVisited(step)) {
            return;
        }
        this.navigationEmitter.emit(step);
    }

    protected onKeyDown(event: KeyboardEvent): void {
        if (!this.navigationEmitter) {
            return;
        }

        if (this.forwardKeys.some((code) => event.code === code) && this.hasVisited(this.activatedIndex + 1)) {
            event.preventDefault();
            this.navigationEmitter.emit(this.activatedIndex + 1);
        }
        if (this.backwardKeys.some((code) => event.code === code) && this.hasVisited(this.activatedIndex - 1)) {
            event.preventDefault();
            this.navigationEmitter.emit(this.activatedIndex - 1);
        }
    }

    protected getAriaTitleText(): string {
        const stepLabel =
            this.stepComponents && this.stepComponents[this.activatedIndex] ? this.stepComponents[this.activatedIndex].label : '';

        return this.contentService.get('stepper-header.aria-label', {
            title: stepLabel,
            currentStep: (this.activatedIndex + 1).toString(),
            totalSteps: this.visitStates.length.toString()
        });
    }

    private updateInternalVisitedState(activeIndex: number): void {
        this.visitStates.forEach((value: boolean, index: number) => {
            if (index <= activeIndex && !value) {
                this.visitStates[index] = true;
            }
        });
    }

    private setStepComponents(apSteps: any[]): void {
        this.visitStates = [];
        apSteps.forEach((apStep: any) => {
            this.visitStates.push(apStep.completed);
        });
        this.stepComponents = apSteps;
        this.activeIndex = this.activatedIndex;
    }

    private setVisitedState(steps: boolean[]): void {
        this.visitStates = steps;
        this.activeIndex = this.activatedIndex;
    }
}
